import React, { useState } from 'react';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { makeStyles } from '@material-ui/styles';
import { colors } from '@skyslope/mache';
import cs from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { type LaunchDarklyFlags, withLaunchDarkly } from '@/common/launchDarkly';
import { useGetUserAndAccountSettings } from '@/lib/api/_hooks';
import useUserInfo from '../auth/useUserInfo';
import { useIsSkySlopeMobileApp } from '../common/utils';
import Spinner from '../components/common/Spinner';
import Header from '../components/pageFrame/Header';
import { useDS3Access } from '../hooks/trial/use-ds3-access';
import { ResourceNames } from '../lib/constants';
import { useShallowSelector, useDispatch } from '../lib/reduxHooks';
import { UserOrigins } from '../lib/types';
import { hideErrorToast, fetchGlobalToken } from '../store/pageFrame/actions';

const useStyles = makeStyles(
  () => ({
    error: {
      backgroundColor: colors.error,
    },
    success: {
      backgroundColor: '#308252',
    },
  }),
  { name: 'PageFrame' }
);

function PageFrame(props: { children: any; flags: LaunchDarklyFlags }) {
  const { children, flags } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  const { loading, errorShow, msg, successShow, settingsFetched, settings, senderBuildResource, fetchedGlobalToken } =
    useShallowSelector((state) => ({
      loading: state.pageFrame.loading,
      errorShow: state.pageFrame.errorShow,
      msg: state.pageFrame.msg,
      successShow: state.pageFrame.successShow,
      settingsFetched: state.pageFrame.settingsFetched,
      settings: state.pageFrame.settings,
      senderBuildResource: state.senderBuild.resource,
      fetchedUserSubscription: state.pageFrame.fetchedUserSubscription,
      fetchedGlobalToken: state.pageFrame.fetchedGlobalToken,
    }));

  // `state.pageFrame.settings` is currently used in many places throughout the app.
  // In addition to fetching and storing the data, this hook also updates the redux state.
  // TODO: Migrate away from redux for this.
  useGetUserAndAccountSettings();

  const isMobileApp = useIsSkySlopeMobileApp();
  const userInfo = useUserInfo();
  const [headerSize, setHeaderSize] = useState(0);

  const isOnSubscriptionPage = pathname.includes('subscriptions');
  const isOnSubRequiredPage =
    pathname.includes('documents') ||
    pathname.includes('recipients') ||
    pathname.includes('properties') ||
    senderBuildResource === ResourceNames.ENVELOPE;

  const {
    hasDs3Access,
    isLoading,
    isTrialExpired,
    showTrialBanner,
    dismissTrialBanner,
    daysLeft,
    isEligibleForExtension,
    latestTrialExtension,
    showCancelledBanner,
    dismissCancelledBanner,
    isActiveCancelledSubscription,
  } = useDS3Access(flags);

  React.useEffect(() => {
    if (!fetchedGlobalToken && settingsFetched) {
      dispatch(fetchGlobalToken());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [fetchedGlobalToken, settingsFetched]);

  React.useEffect(() => {
    if (userInfo !== null && !userInfo.isPrimeAuthenticated) {
      localStorage.setItem('_isStandaloneUser', 'true');
    }
  }, [userInfo]);

  React.useEffect(() => {
    if (isLoading) return;

    const shouldRedirectToSubscriptions =
      !hasDs3Access && !isLoading && isOnSubRequiredPage && !isMobileApp && isTrialExpired;

    if (shouldRedirectToSubscriptions) {
      const previousUrl = window.location.href;
      history.push({
        pathname: '/subscriptions',
        state: { url: previousUrl },
      });
    } else if (hasDs3Access && isOnSubscriptionPage) {
      if (document.referrer.includes('send.skyslope') && !document.referrer.includes('subscriptions')) {
        history.goBack();
      } else {
        history.push({ pathname: '/envelopes' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [isLoading, hasDs3Access, isTrialExpired, hasDs3Access, isOnSubRequiredPage, isOnSubscriptionPage]);

  return (
    <div className="flex flex-col">
      <Header
        showTrialBanner={showTrialBanner}
        handleDismissBanner={dismissTrialBanner}
        daysLeft={daysLeft}
        isTrialExpired={Boolean(isTrialExpired)}
        setHeaderSize={setHeaderSize}
        redirectHeader={
          settingsFetched && (!settings?.account.userOrigin || settings?.account.userOrigin === UserOrigins.Prime)
        }
        isEligibleForExtension={Boolean(isEligibleForExtension)}
        latestTrialExtension={latestTrialExtension}
        showCancelledBanner={showCancelledBanner}
        handleDismissCancelledBanner={dismissCancelledBanner}
        isActiveCancelledSubscription={isActiveCancelledSubscription}
      />
      <div
        className={cs('flex flex-1 justify-center overflow-auto w-full', {
          'relative py-6': !isMobileApp,
          absolute: isMobileApp,
          'p-0': showTrialBanner || showCancelledBanner,
        })}
        style={{ minHeight: `calc(100vh - ${headerSize}px - 48px)` }}
      >
        {loading && <Spinner />}
        {children}
      </div>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={errorShow || successShow}
        autoHideDuration={errorShow ? 4_000 : 1_500}
        onClose={(e, reason) => {
          if (reason === 'timeout') dispatch(hideErrorToast());
        }}
      >
        <SnackbarContent
          className={successShow ? classes.success : errorShow ? classes.error : ''}
          message={
            successShow ? (
              <div className="flex">
                <CheckCircleIcon className="mr-2" />
                {msg}
              </div>
            ) : errorShow ? (
              <>{msg}</>
            ) : null
          }
        />
      </Snackbar>
    </div>
  );
}

const withLd = withLaunchDarkly(PageFrame);
export { withLd as PageFrame };
